import { createApp } from "vue"
import { createPinia } from "pinia"
import * as Sentry from "@sentry/vue"

import App from "@/App.vue"
import AppRouter from "@/AppRouter"
import i18n from "@/lang/i18n"
import { Page } from "@/graphql"
import { analytics } from "@/utils/analytics"
import { useMediaQuery } from "@vueuse/core"

import "normalize.css"
import "@/assets/styles/main.css"

import { UseRootStore } from "./src/model/RootStore.ts"
import { createProjectStore, ProjectStoreKey } from "./src/stores/projectStore.ts"
import { appVersion } from "./src/AppConfig.ts"
import { getDefaultPageRoute } from "./src/utils/defaultPageRouting.ts"
import { Route } from "./src/utils/routing.ts"
import { usePrivacyStore } from "@/stores/privacyStore"
import { TrackingPurpose } from "@/types/privacyChoices"
import { startPrivacyTracking } from "@/utils/privacyTracking"
import { isPrivateRoute } from "@/utils/trackingLocation"
import { createPrivacySentryTransport } from "@/utils/sentryPrivacyTransport"
import { createPrivacyReplayIntegration } from "@/utils/sentryReplay"

const app = createApp(App)

app.use(createPinia())
app.use(AppRouter)
app.use(i18n)

const isLargeScreen = useMediaQuery("(min-width: 1024px)")
const rootStore = UseRootStore()
const privacy = usePrivacyStore()

const useProjectStore = createProjectStore()

async function navigateToDefaultPageOnStartup(): Promise<void> {
	const currentRoute = AppRouter.currentRoute.value

	if (currentRoute.name !== Route.Home || Object.keys(currentRoute.query).length > 0 || currentRoute.hash) {
		return
	}

	await rootStore.fetchBasicCurrentUserDetails()
	const defaultPage = rootStore.CurrentUserBasicDetails?.defaultPage

	if (!defaultPage || defaultPage === Page.Exploration) {
		return
	}

	const defaultPageRoute = getDefaultPageRoute(defaultPage)

	await AppRouter.replace(defaultPageRoute)
}

app.provide("isLargeScreen", isLargeScreen)
app.provide(ProjectStoreKey, useProjectStore)
app.provide("useRootStore", UseRootStore)

function startUserApiTokenRefreshLoop(): void {
	window.setInterval(
		() => {
			rootStore.refreshUserApiTokenIfNeeded().catch((error: unknown) => {
				console.warn("Could not refresh Landerz access token:", error)
			})
		},
		5 * 60 * 1000
	)

	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState !== "visible") {
			return
		}

		rootStore.refreshUserApiTokenIfNeeded().catch((error: unknown) => {
			console.warn("Could not refresh Landerz access token:", error)
		})
	})
}

const environment = import.meta.env.MODE
const isDev = environment === "development"

app.config.errorHandler = err => {
	analytics.captureException(err)
}

// similar to backend
Sentry.init({
	app,

	dsn: isDev ? undefined : "https://123581839d5e4cfd925e916ed90aa68f@o733221.ingest.sentry.io/4505003190910976",

	environment: environment,
	debug: false,

	release: appVersion,

	// Initial scope with runtime information
	initialScope: {
		tags: {
			runtime: "browser",
			runtimeVersion: navigator.userAgent,
			environment: environment,
		},
		contexts: {
			app: {
				name: "landerz-frontend",
				version: appVersion,
			},
			browser: {
				name: navigator.userAgent,
				url: window.location.origin + window.location.pathname,
			},
		},
	},

	// Integrations
	integrations: [Sentry.browserTracingIntegration({ router: AppRouter }), createPrivacyReplayIntegration()],

	tracePropagationTargets: ["localhost", "app-staging.landerz.ca", /^\//],
	tracesSampler: () => (privacy.choices[TrackingPurpose.UsageStatistics] && !isPrivateRoute() ? 1 : 0),
	replaysSessionSampleRate: 0,
	replaysOnErrorSampleRate: 0,

	// Check permission again at dispatch: an async capture/stop can finish after
	// withdrawal. Basic error diagnostics remain separate from optional telemetry.
	transport: options => createPrivacySentryTransport(options, () => privacy.choices),

	// Before send hook to sanitize sensitive data
	beforeSend(event) {
		if (event.request?.url) {
			try {
				const url = new URL(event.request.url, window.location.origin)
				event.request.url = url.origin + url.pathname
			} catch {
				event.request.url = "[MASKED]"
			}
		}
		// Sanitize sensitive information
		const headers = event.request?.headers
		if (headers) {
			Object.keys(headers).forEach(header => {
				if (["authorization", "x-api-token"].includes(header.toLowerCase())) {
					headers[header] = "[MASKED]"
				}
			})
		}

		return event
	},

	// Before breadcrumb hook
	beforeBreadcrumb(breadcrumb) {
		if (breadcrumb.data?.url) {
			try {
				const url = new URL(breadcrumb.data.url, window.location.origin)
				breadcrumb.data.url = url.origin + url.pathname
			} catch {
				breadcrumb.data.url = "[MASKED]"
			}
		}
		return breadcrumb
	},
})

window.addEventListener("error", event => {
	Sentry.captureException(event.error, {
		tags: {
			component: "GlobalError",
			error_type: "unhandled_error",
		},
		extra: {
			error_message: event.message,
			error_filename: event.filename,
			error_lineno: event.lineno,
			error_colno: event.colno,
			user_agent: navigator.userAgent,
			url: window.location.origin + window.location.pathname,
		},
	})
})

window.addEventListener("unhandledrejection", event => {
	Sentry.captureException(new Error(event.reason), {
		tags: {
			component: "GlobalError",
			error_type: "unhandled_promise_rejection",
		},
		extra: {
			reason: event.reason,
			user_agent: navigator.userAgent,
			url: window.location.origin + window.location.pathname,
		},
	})
})

void AppRouter.isReady().then(async () => {
	await rootStore.refreshUserApiTokenIfNeeded().catch((error: unknown) => {
		console.warn("Could not refresh Landerz access token during startup:", error)
	})
	rootStore.initializeDefaultLayers()

	if (rootStore.isUserLoggedIn) {
		rootStore.identifyCurrentUser()
		await navigateToDefaultPageOnStartup()
	}

	startUserApiTokenRefreshLoop()
	const disposePrivacyTracking = startPrivacyTracking(
		privacy,
		() => (rootStore.isUserLoggedIn ? `account:${rootStore.currentUser.getInfo()?.id ?? "unknown"}` : "guest"),
		() => rootStore.identifyCurrentUser(),
		() => AppRouter.currentRoute.value.fullPath,
		() => !rootStore.isClearingSession
	)
	import.meta.hot?.dispose(disposePrivacyTracking)
	app.mount("#app")
})
